//
// Copyright (c) 2021 Astute Global, Inc.
//

import i18next from "i18n/i18n";
import {
  ButtonView,
  createLabeledInputText,
  CssTransitionDisablerMixin,
  IconCancel,
  IconCheck,
  LabeledFieldView,
  submitHandler,
  View,
} from "ckeditor5";
import TemplatePreviewView from "./templatePreviewView";
import { removeCkReset } from "../../ckEditorUtil";

import "../css/commonTemplateView.css";
import "../css/createTemplateView.css";

class BaseCreateTemplateView extends View {
  constructor(editor, command) {
    super(editor.locale);
    this.editor = editor;
    this.command = command;
    this.instructions = this._createInstructions();
    this.nameView = this._createNameView();
    this.templatePreview = this._createTemplatePreview();
    this.okButton = this._createOkButton();
    this.cancelButton = this._createCancelButton();

    this.setTemplate({
      tag: "div",
      attributes: {
        class: [
          "ck",
          "ck-responsive-form",
          "ck-vertical-form",
          "custom-template-view",
          "custom-template-create-view",
        ],
      },
      children: this._createFormChildren(),
    });
  }

  get templateName() {
    if (this.nameView && this.nameView.fieldView && this.nameView.fieldView.element) {
      return this.nameView.fieldView.element.value;
    }
    return "";
  }

  focus() {
    if (this.nameView && this.nameView.fieldView) {
      this.nameView.fieldView.focus();
    }
  }

  render() {
    super.render();
    // Remove the CKEditor reset CSS classes, to better integrate with Marionette UI
    setTimeout(() => removeCkReset(this.element), 100);
    submitHandler({
      view: this,
    });
  }

  reset() {
    this.nameView.fieldView.value = "";
  }

  _createCancelButton() {
    const button = new ButtonView(this.locale);
    button.set({
      label: i18next.t("common.cancel"),
      icon: IconCancel,
      tooltip: true,
    });
    button.extendTemplate({
      attributes: {
        class: "ck-button-cancel",
      },
    });
    button.delegate("execute").to(this, "cancel");
    return button;
  }

  _createFormChildren() {
    return this.createCollection([
      this.instructions,
      this.nameView,
      this.templatePreview,
      this.okButton,
      this.cancelButton,
    ]);
  }

  _createInstructions() {
    const instructionsView = new View(this.locale);
    instructionsView.setTemplate({
      tag: "div",
      attributes: {
        class: "sub-header",
      },
      children: [
        {
          text: i18next.t("ckeditor.customTemplate.createInstructions"),
        },
      ],
    });
    return instructionsView;
  }

  _createNameView() {
    const nameView = new LabeledFieldView(this.locale, createLabeledInputText);
    nameView.class = "custom-template-name-field";
    nameView.label = i18next.t("ckeditor.customTemplate.templateName");
    nameView.fieldView.extendTemplate({
      attributes: {
        required: true,
      },
    });
    nameView
      .bind("errorText")
      .to(nameView, "isEmpty", (value) =>
        value ? i18next.t("ckeditor.customTemplate.noName") : "",
      );
    return nameView;
  }

  _createOkButton() {
    const button = new ButtonView(this.locale);
    button.set({
      label: i18next.t("common.ok"),
      icon: IconCheck,
      tooltip: true,
    });
    button.extendTemplate({
      attributes: {
        class: "ck-button-save",
      },
    });
    button.type = "submit";
    button.delegate("execute").to(this, "submit");
    return button;
  }

  _createTemplatePreview() {
    const labeledFieldView = new LabeledFieldView(
      this.locale,
      (_parentView, viewUid, statusUid) => {
        const view = new TemplatePreviewView(this.locale, viewUid, statusUid);
        view.bind("rawHtml").to(this.command, "value");
        return view;
      },
    );
    labeledFieldView.class = "template-preview-field";
    labeledFieldView.label = i18next.t("ckeditor.customTemplate.templatePreview");
    labeledFieldView.isEmpty = false;
    return labeledFieldView;
  }
}

const CreateTemplateView = CssTransitionDisablerMixin(BaseCreateTemplateView);

export default CreateTemplateView;
